import classNames from 'classnames';
import React, { ReactElement, useState, useContext } from 'react';

import AppContext from '../../context';
import Multilanguage from '../Multilanguage';

import BurgerHeader from './BurgerHeader';
import styles from './BurgerNavigation.module.scss';
import { convertPanelMap, ROOT_PATH } from './BurgerNavigationConverter';
import BurgerPanel from './BurgerPanel';
import { PanelMap } from './types';

export const MULTILANGUAGE_BAR_HEIGHT = 56;
export const MIDDLE_BAR_HEIGHT = 60;

export type BurgerNavigationProps = {
    handleCloseClick: () => void;
    open: boolean;
};

export default function BurgerNavigation({
    handleCloseClick,
    open,
}: BurgerNavigationProps): ReactElement | null {
    const { categories, translations, multilanguage } = useContext(AppContext);

    const [panelsMap, setPanelsMap] = useState<PanelMap>(
        convertPanelMap(
            categories,
            translations.get('shop.header.categories.title.link'),
        ),
    );

    if (!panelsMap.size) {
        return null;
    }

    const openPanels = Array.from(panelsMap.values()).filter(
        (panel) => panel.show,
    );

    const onClickNextPanel = (nextPanelPath: string): void => {
        const nextPanel = panelsMap.get(nextPanelPath);
        if (nextPanel) {
            nextPanel.show = true;
        }
        setPanelsMap(new Map(panelsMap));
    };

    const onClickPrevPanel = (currentPanelPath: string): void => {
        const currentPanel = panelsMap.get(currentPanelPath);
        if (currentPanel) {
            currentPanel.show = false;
        }
        setPanelsMap(new Map(panelsMap));
    };

    const currentPanel = openPanels.slice(-1)[0];
    const panelWrapperHeight =
        MIDDLE_BAR_HEIGHT + (multilanguage ? MULTILANGUAGE_BAR_HEIGHT : 0);

    const handleClose = () => {
        // reset the panels when close trigger
        openPanels.forEach((panel) => {
            const currentPanel = panelsMap.get(panel.currentPath);
            if (currentPanel) {
                currentPanel.show = panel.currentPath === ROOT_PATH;
            }
        });
        setPanelsMap(new Map(panelsMap));
        handleCloseClick();
    };

    return (
        <>
            <div
                data-zta="hamburger-navigation-wrapper"
                className={classNames(styles.mobileBurgerNavOverlay, {
                    [styles.open]: open,
                })}
                onClick={handleClose}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        handleClose();
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label={translations.get('general.button.close') ?? ''}
            />
            <nav
                className={classNames(styles.mobileBurgerNav, {
                    [styles.open]: open,
                })}
                data-zta="hamburger-navigation"
                aria-hidden={!open}>
                <BurgerHeader
                    currentPanel={currentPanel}
                    onClickPrevPanel={onClickPrevPanel}
                    translations={translations}
                    onCloseClick={handleClose}
                    isOpen={open}
                />
                <div
                    className={styles.burgerPanelsWrapper}
                    data-zta="burger-panels-wrapper"
                    style={
                        {
                            '--header-panel-wrapper-height': `calc(100% - ${panelWrapperHeight}px)`,
                            '--header-panel-wrapper-width': `${openPanels.length * 100}%`,
                            '--header-panel-wrapper-left': `${(openPanels.length - 1) * -100}%`,
                        } as React.CSSProperties
                    }>
                    <BurgerPanel
                        onClickNextPanel={onClickNextPanel}
                        openPanels={openPanels}
                    />
                </div>
                {multilanguage && <Multilanguage />}
            </nav>
        </>
    );
}
